import ApiService from './api';

/**
 * Represents the schema for a Changelog Entity.
 */
export interface ChangelogEntity {
  /** Document ID from MongoDB (optional). */
  _id?: string;
  /** The type of changelog entry. */
  type: string;
  /** The ID of the associated bot. */
  botId: string;
  /** The name of the changelog entry. */
  name: string;
  /** The channel for the changelog entry (nullable). */
  channel: string | null;
  /** Additional comments (nullable). */
  comment: string | null;
  /** The user associated with the changelog entry. */
  user: string;
  /** JSON payload (optional). */
  jsonPayload?: string;
  /** Document version from MongoDB (optional). */
  __v?: string;
}

/** Parameters accepted when searching changelog entries. */
export interface ChangelogSearchParams {
  /** The type of changelog entry. */
  type: string;
  /** The ID of the associated bot. */
  botId: string;
  /** The name of the changelog entry. */
  name: string;
  /** The channel to filter by (null for the default channel). */
  channel: string | null;
  /** The user associated with the changelog entry. */
  user: string;
  /** JSON payload to match (optional). */
  jsonPayload?: string;
}

const ChangelogService = {
  /**
   * Create a new changelog entry.
   *
   * @param changelog - The changelog object to be created.
   * @returns A promise that resolves to the response data or an error object.
   */
  createChangelog: async function(changelog: Record<string, any>): Promise<[ChangelogEntity] | { error: any }> {
    /**
     * Send as FormData instead of JSON since payload can grow too big for API.
     */
    const form = new FormData();
    for (const key in changelog) {

      // Special handling: Channel/Comment may be null for default, but it gets converted to the string null
      if (['channel', 'comment'].includes(key) && changelog[key] === null) {
        form.append(key, '');
        continue;
      }

      form.append(key, changelog[key]);
    }

    try {
      const response = await ApiService.postData(`/schaltzentrale/changelog`, form);
      return response.data;
    } catch (error) {
      return { error };
    }
  },

  /**
   * Search for changelog entries based on specified parameters.
   *
   * @param searchParams - Search parameters for the changelog.
   * @returns A promise that resolves to the response data or an error object.
   */
  searchChangelogByParams: async function(searchParams: ChangelogSearchParams): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/changelog/searchByParams`, searchParams);
      return response.data;
    } catch (error) {
      console.log(error)
      return { error };
    }
  },
};

export default ChangelogService;

export { ChangelogService };
